import { ApiErrorState } from "./ApiErrorState";
import type { ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Icon, IconButton, SpinnerIcon, Stack } from "@wato/ui";

/** Switches AR ⇄ EN live (the provider flips `<html lang dir>`). */
export function LanguageToggle({ className }: { readonly className?: string } = {}) {
  const { t, locale, setLocale } = useI18n();
  const other = locale === "ar" ? "en" : "ar";
  return (
    <Button
      variant="secondary"
      size="sm"
      lang={other}
      className={className}
      onClick={() => setLocale(other)}
    >
      {t("common.switchLanguage")}
    </Button>
  );
}

export function PageLoading() {
  const { t } = useI18n();
  return (
    <div role="status" className="flex min-h-40 items-center justify-center gap-2 text-fg-muted">
      <SpinnerIcon className="size-5" />
      <span className="text-sm">{t("dashboard.shell.loading")}</span>
    </div>
  );
}

/**
 * UXR-33: loading skeleton for list pages (header bar + rows). Announced once to assistive tech;
 * the pulse animation is the only motion and is neutral token colour.
 */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  const { t } = useI18n();
  return (
    <div role="status" aria-busy="true" data-list-skeleton className="flex flex-col gap-3">
      <span className="sr-only">{t("dashboard.shell.listLoading")}</span>
      <div aria-hidden="true" className="h-8 w-1/3 animate-pulse rounded-md bg-surface-muted" />
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="h-12 animate-pulse rounded-md border border-line bg-surface-muted"
        />
      ))}
    </div>
  );
}

export function PageError({
  message,
  onRetry,
  error,
  workspaceId,
}: {
  message?: string | undefined;
  onRetry?: () => void;
  /** The thrown value: when given, the message is mapped from its API error code (UX-23). */
  error?: unknown;
  workspaceId?: string | undefined;
}) {
  const { t } = useI18n();
  if (error !== undefined && error !== null)
    return (
      <ApiErrorState error={error} workspaceId={workspaceId} {...(onRetry ? { onRetry } : {})} />
    );
  return (
    <Alert
      tone="error"
      title={t("dashboard.shell.loadError")}
      actions={
        onRetry ? (
          <IconButton
            label={t("dashboard.shell.retry")}
            icon={<Icon name="refresh" />}
            variant="secondary"
            onClick={onRetry}
          />
        ) : undefined
      }
    >
      {message}
    </Alert>
  );
}

/** Centered card used by the sign-in / sign-up / 2FA / reset pages. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-surface-muted">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-[var(--size-content)] items-center justify-between gap-4 px-6 py-3">
          <span className="flex items-center gap-2" data-testid="auth-brand">
            <img src="/icons/icon-192.png" alt="" width={32} height={32} className="rounded-md" />
            <span className="text-lg font-semibold text-fg">{t("dashboard.b29b.auth.brand")}</span>
          </span>
          <LanguageToggle />
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-md flex-col gap-6 px-6 py-10">
        <Stack gap={1}>
          <h1 className="text-2xl font-semibold text-fg">{title}</h1>
          {subtitle && <p className="text-sm text-fg-muted">{subtitle}</p>}
        </Stack>
        <div className="rounded-lg bg-surface p-6 shadow-sm">{children}</div>
        {footer && <div className="text-center text-sm text-fg-muted">{footer}</div>}
      </main>
    </div>
  );
}

/** Only same-app relative paths are accepted as a post-login destination (no open redirect). */
export function safeNextPath(value: string | null): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
    return null;
  }
  return value;
}
